<template>
  <div class="fade-in p-3 sm:p-4 max-w-screen-xl w-full mx-auto">
    <div class="h-full">
      <!-- Back button and title -->
      <div
        class="flex flex-col sm:flex-row sm:items-center mb-4 sm:mb-2 space-y-3 sm:space-y-0 sm:space-x-2"
      >
        <div class="flex items-center">
          <ChartNoAxesCombined class="w-5 h-5 mr-2 text-gray-700" />
          <h1 class="text-xl font-normal text-gray-800 min-w-[120px]">
            {{ batchName }} Program Statuses
          </h1>
        </div>
      </div>

    <div class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-x-auto overflow-y-hidden mt-4">
      <div v-if="isLoading" class="flex items-center justify-center py-20">
        <OuttalentLoader />
      </div>
      <div v-else-if="adventurers.length > 0">
        <BatchProgressTable :batch-name="batchName" :adventurers="adventurers" />
      </div>
      <div v-else class="flex flex-col items-center justify-center py-20 text-center">
        <div class="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-4">
          <TentTree class="w-8 h-8 text-gray-400" />
        </div>
        <h3 class="text-base font-medium text-gray-900 mb-1">No adventurers found</h3>
        <p class="text-sm text-gray-500">There are no adventurers in this expedition yet.</p>
      </div>
    </div>
  </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, computed } from 'vue'
import { useRoute } from 'vue-router'
import { TentTree, ChartNoAxesCombined } from 'lucide-vue-next'
import BatchProgressTable from '@/components/manage/BatchProgressTable.vue'
import OuttalentLoader from '@/components/common/OuttalentLoader.vue'
import { useExpeditionsStore } from '@/stores/expeditions'
import { formatExpeditionName } from '@/utils/formatExpeditionName'
import api from '@/utils/api'
import { API_ROUTES } from '@/const/apiRoutes'

const route = useRoute()
const expeditionsStore = useExpeditionsStore()

const isLoading = ref(true)
const adventurers = ref<any[]>([])

const expeditionId = computed(() => route.params.id as string)

const batchName = computed(() => {
  const progress = expeditionsStore.adventurersProgress as any
  return formatExpeditionName(progress?.name) || `Expedition ${expeditionId.value}`
})

const fetchData = async () => {
  isLoading.value = true
  try {
    // Fetch expedition data to get the batch name for display
    await expeditionsStore.fetchAdventurersProgress(expeditionId.value)

    // Fetch adventurers by expedition ID
    const response = await api.get(API_ROUTES.GET_ADVENTURERS_BY_EXPEDITION(expeditionId.value))
    adventurers.value = response.data || []
  } catch (error) {
    console.error('Error fetching expedition data:', error)
    adventurers.value = []
  } finally {
    isLoading.value = false
  }
}

onMounted(() => {
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  fetchData()
})

// Watch for route changes
watch(
  () => route.params.id,
  () => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    fetchData()
  }
)
</script>
